/**
 * AdminContractPatchForm — modal to PATCH pudo_onboarding_agents.contract
 * Phase 2D P4 (2026-04-24)
 *
 * PATCH /admin/field-agents/{agent_id}/contract
 * Body: { start_date?, end_date?, renewal_note? }
 */
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, X as XIcon, CalendarClock } from "lucide-react";

export default function AdminContractPatchForm({ agent, open, onClose, onSaved }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const c = agent?.contract || {};
    setStart(c.start_date || agent?.contract_start_date || "");
    setEnd(c.end_date || agent?.contract_end_date || "");
    setNote(c.renewal_note || "");
  }, [open, agent]);

  if (!open || !agent) return null;

  const save = async () => {
    if (start && end && start > end) { toast.error("start_date must be ≤ end_date"); return; }
    setBusy(true);
    try {
      const payload = {};
      if (start) payload.start_date = start;
      if (end) payload.end_date = end;
      if (note) payload.renewal_note = note;
      const res = await api.patch(`/admin/field-agents/${agent.id}/contract`, payload);
      toast.success("Contract updated");
      onSaved && onSaved(res.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Contract update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="contract-patch-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <CalendarClock className="w-4 h-4 text-indigo-600" />
            Contract · {agent.name || agent.id}
          </div>
          <button className="text-slate-500 hover:text-slate-900" onClick={onClose} data-testid="contract-patch-close">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-slate-500">Start date</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="contract-patch-start" />
          </div>
          <div>
            <label className="text-xs text-slate-500">End date</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="contract-patch-end" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Renewal note</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. monthly renewal" data-testid="contract-patch-note" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="contract-patch-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="contract-patch-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
